// overlayUtils.js
// Overlay measurement and drawing utilities for screenshot rendering.
//
// Copyright Viktor Eberhardt, 2025
// Author: Viktor Eberhardt

// Draw label image at explicit position (x,y is top-left of the framed box)
export function drawLabelOverlayAt(
  canvas,
  labelImg,
  x,
  y,
  measured,
  opts = {}
) {
  if (!canvas || !labelImg || !measured) return canvas;
  const ctx = canvas.getContext("2d");
  const {
    bgColor = "rgba(255,255,255,0.95)",
    borderColor = "rgba(0,0,0,0.2)",
  } = opts;
  const { dw, dh, w, h } = measured;
  ctx.save();
  ctx.fillStyle = bgColor;
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = 1;
  ctx.fillRect(x, y, w, h);
  ctx.strokeRect(x + 0.5, y + 0.5, w, h);
  ctx.drawImage(labelImg, x + 2, y + 2, dw, dh);
  ctx.restore();
  return canvas;
}

// Measure the label overlay box size for a given canvas and image
export function measureLabelOverlay(canvas, labelImg, opts = {}) {
  if (!canvas || !labelImg) return { w: 0, h: 0, dw: 0, dh: 0 };
  const { maxWidthFrac = 0.28, maxHeightFrac = 0.28 } = opts;
  const cw = canvas.width | 0;
  const ch = canvas.height | 0;
  if (cw <= 0 || ch <= 0) return { w: 0, h: 0, dw: 0, dh: 0 };
  const iw = labelImg.naturalWidth || labelImg.width;
  const ih = labelImg.naturalHeight || labelImg.height;
  if (!iw || !ih) return { w: 0, h: 0, dw: 0, dh: 0 };
  const maxW = Math.max(64, Math.floor(cw * maxWidthFrac));
  const maxH = Math.max(64, Math.floor(ch * maxHeightFrac));
  const scale = Math.min(maxW / iw, maxH / ih, 1);
  const dw = Math.max(1, Math.floor(iw * scale));
  const dh = Math.max(1, Math.floor(ih * scale));
  // Include 2px frame
  return { w: dw + 4, h: dh + 4, dw, dh };
}

// Draw file name text as a badge at explicit position
export function drawFileNameOverlay(canvas, text, opts = {}) {
  if (!canvas || !text) return canvas;
  const ctx = canvas.getContext("2d");
  const {
    margin = 8,
    paddingX = 6,
    paddingY = 4,
    fontSize = 13,
    fontFamily = "sans-serif",
    textColor = "#fff",
    bgColor = "rgba(0,0,0,0.6)",
    borderColor = "rgba(255,255,255,0.2)",
    borderWidth = 0,
    maxWidthFrac = 0.9,
    corner = "top-left",
    fixedPos = null, // if provided, overrides corner placement
  } = opts;

  const cw = canvas.width | 0;
  const ch = canvas.height | 0;
  if (cw <= 0 || ch <= 0) return canvas;

  ctx.save();
  ctx.font = `${fontSize}px ${fontFamily}`;
  ctx.textBaseline = "top";

  const maxTextWidth = Math.floor(cw * maxWidthFrac) - (margin + paddingX) * 2;
  let displayText = text;
  let metrics = ctx.measureText(displayText);
  if (metrics.width > maxTextWidth && maxTextWidth > 20) {
    const ell = "…";
    let lo = 0,
      hi = displayText.length;
    while (lo < hi) {
      const mid = ((lo + hi) / 2) | 0;
      const candidate = displayText.slice(0, mid) + ell;
      if (ctx.measureText(candidate).width <= maxTextWidth) lo = mid + 1;
      else hi = mid;
    }
    displayText = displayText.slice(0, Math.max(0, lo - 1)) + ell;
    metrics = ctx.measureText(displayText);
  }

  const boxW = Math.ceil(metrics.width + paddingX * 2);
  const boxH = Math.ceil(fontSize + paddingY * 2);
  let x = margin;
  let y = margin;
  if (fixedPos) {
    x = fixedPos.x | 0;
    y = fixedPos.y | 0;
  } else {
    if (corner.includes("right")) x = Math.max(margin, cw - margin - boxW);
    if (corner.includes("bottom")) y = Math.max(margin, ch - margin - boxH);
  }

  ctx.fillStyle = bgColor;
  ctx.fillRect(x, y, boxW, boxH);
  if (borderWidth > 0) {
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = borderWidth;
    ctx.strokeRect(x + 0.5, y + 0.5, boxW, boxH);
  }

  ctx.fillStyle = textColor;
  ctx.fillText(displayText, x + paddingX, y + paddingY);
  ctx.restore();
  return canvas;
}

// Measure file name badge box for stacking
export function measureFileNameOverlay(canvas, text, opts = {}) {
  if (!canvas || !text) return { w: 0, h: 0 };
  const ctx = canvas.getContext("2d");
  const {
    paddingX = 6,
    paddingY = 4,
    fontSize = 13,
    fontFamily = "sans-serif",
    maxWidthFrac = 0.9,
  } = opts;
  const cw = canvas.width | 0;
  ctx.save();
  ctx.font = `${fontSize}px ${fontFamily}`;
  const maxTextWidth = Math.floor(cw * maxWidthFrac) - paddingX * 2;
  let displayText = text;
  let metrics = ctx.measureText(displayText);
  if (metrics.width > maxTextWidth && maxTextWidth > 20) {
    const ell = "…";
    let lo = 0,
      hi = displayText.length;
    while (lo < hi) {
      const mid = ((lo + hi) / 2) | 0;
      const candidate = displayText.slice(0, mid) + ell;
      if (ctx.measureText(candidate).width <= maxTextWidth) lo = mid + 1;
      else hi = mid;
    }
    displayText = displayText.slice(0, Math.max(0, lo - 1)) + ell;
    metrics = ctx.measureText(displayText);
  }
  ctx.restore();
  return {
    w: Math.ceil(metrics.width + paddingX * 2),
    h: Math.ceil(fontSize + paddingY * 2),
  };
}

// Measure scale bar bounding box for stacking
export function measureScaleBar(data) {
  const width = Math.max(10, Math.floor(data?.width || 100));
  // Slightly thicker line and taller caps to match in-renderer look better
  const fontSize = 14;
  const labelOffset = 6;
  const capH = 16; // full stop height centered on the line
  const height = fontSize + labelOffset + capH;
  return { w: width, h: height };
}

import { drawScaleBarGlyph } from "../../common/utils/ScaleBarDraw";

// Draw scale bar at explicit top-left box coordinates
export function drawScaleBarAt(canvas, data, x, y, corner = "bottom-right") {
  if (!canvas || !data) return canvas;
  const ctx = canvas.getContext("2d");
  const color = data.color || "#fff";
  const width = Math.max(10, Math.floor(data.width || 100));
  const label = data.label || "";
  const labelOffset = 6;
  const capH = 16;
  const isBottom = corner.includes("bottom");
  const yLine = isBottom ? y + (14 + labelOffset + capH) : y + capH; // 14 matches measureScaleBar
  drawScaleBarGlyph(ctx, {
    centerX: x + width / 2,
    centerY: yLine,
    width,
    label,
    color,
    pixelScale: 1, // already in screen pixels
    labelAbove: isBottom, // bottom corners place label above line
  });
  return canvas;
}

// Draw all selected overlays without overlapping, stacked within their corners
export function drawOverlaysStacked(canvas, cfg = {}) {
  const {
    includeScaleBar,
    includeLabel,
    includeFileName,
    includeUser,
    includeTime,
    scaleBarCorner = "bottom-right",
    labelCorner = "top-right",
    fileNameCorner = "top-left",
    userCorner = "bottom-left",
    timeCorner = "bottom-left",
    labelImg,
    scaleBarData,
    fileNameText,
    userText,
    timeText,
  } = cfg;
  if (!canvas) return canvas;
  const cw = canvas.width | 0;
  const ch = canvas.height | 0;
  if (cw <= 0 || ch <= 0) return canvas;
  const margin = 8;
  const gap = 6;

  const groups = {
    "top-left": [],
    "top-right": [],
    "bottom-right": [],
    "bottom-left": [],
  };

  if (includeFileName && fileNameText) {
    const size = measureFileNameOverlay(canvas, fileNameText, {});
    groups[fileNameCorner].push({ kind: "filename", size, text: fileNameText });
  }
  if (includeUser && userText) {
    const size = measureFileNameOverlay(canvas, userText, {});
    groups[userCorner].push({ kind: "user", size, text: userText });
  }
  if (includeTime && timeText) {
    const size = measureFileNameOverlay(canvas, timeText, {});
    groups[timeCorner].push({ kind: "time", size, text: timeText });
  }
  if (includeLabel && labelImg) {
    const measured = measureLabelOverlay(canvas, labelImg, {});
    groups[labelCorner].push({ kind: "label", measured });
  }
  if (includeScaleBar && scaleBarData) {
    const size = measureScaleBar(scaleBarData);
    groups[scaleBarCorner].push({ kind: "scalebar", size });
  }

  // Ensure scale bar is always placed closest to the edge by ordering items
  const weight = { scalebar: 0, label: 1, filename: 2, user: 2, time: 2 };
  Object.keys(groups).forEach((k) => {
    groups[k].sort((a, b) => (weight[a.kind] ?? 99) - (weight[b.kind] ?? 99));
  });

  const placeGroup = (corner, items) => {
    if (!items || items.length === 0) return;
    if (corner.includes("top")) {
      let y = margin;
      for (const it of items) {
        let w = it.size?.w || it.measured?.w || 0;
        let h = it.size?.h || it.measured?.h || 0;
        const x = corner.includes("right")
          ? Math.max(margin, cw - margin - w)
          : margin;
        if (it.kind === "filename") {
          drawFileNameOverlay(canvas, it.text, { fixedPos: { x, y } });
        } else if (it.kind === "user") {
          drawFileNameOverlay(canvas, it.text, { fixedPos: { x, y } });
        } else if (it.kind === "time") {
          drawFileNameOverlay(canvas, it.text, { fixedPos: { x, y } });
        } else if (it.kind === "label") {
          drawLabelOverlayAt(canvas, labelImg, x, y, it.measured, {});
        } else if (it.kind === "scalebar") {
          drawScaleBarAt(canvas, scaleBarData, x, y, corner);
        }
        y += h + gap;
      }
    } else {
      let y = ch - margin;
      for (const it of items) {
        let w = it.size?.w || it.measured?.w || 0;
        let h = it.size?.h || it.measured?.h || 0;
        const x = corner.includes("right")
          ? Math.max(margin, cw - margin - w)
          : margin;
        const yTop = y - h;
        if (it.kind === "filename") {
          drawFileNameOverlay(canvas, it.text, { fixedPos: { x, y: yTop } });
        } else if (it.kind === "user") {
          drawFileNameOverlay(canvas, it.text, { fixedPos: { x, y: yTop } });
        } else if (it.kind === "time") {
          drawFileNameOverlay(canvas, it.text, { fixedPos: { x, y: yTop } });
        } else if (it.kind === "label") {
          drawLabelOverlayAt(canvas, labelImg, x, yTop, it.measured, {});
        } else if (it.kind === "scalebar") {
          drawScaleBarAt(canvas, scaleBarData, x, yTop, corner);
        }
        y -= h + gap;
      }
    }
  };

  ["top-left", "top-right", "bottom-right", "bottom-left"].forEach((c) => {
    placeGroup(c, groups[c]);
  });
  return canvas;
}

export default {
  drawLabelOverlayAt,
  measureLabelOverlay,
  drawFileNameOverlay,
  measureFileNameOverlay,
  measureScaleBar,
  drawScaleBarAt,
  drawOverlaysStacked,
};
